import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import Loading from "@core/layouts/loading/Loading";
import { XIcon } from "lucide-react";
import { cn } from "@core/utils/domUtil";

export interface DialogPropsType extends React.ComponentProps<typeof DialogPrimitive.Root> {
  children?: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?(open: boolean): void;
  modal?: boolean;
}

interface DialogTriggerPropsType extends React.ComponentProps<typeof DialogPrimitive.Trigger> {
  asChild?: boolean;
  children?: React.ReactNode;
}

interface DialogPortalPropsType extends React.ComponentProps<typeof DialogPrimitive.Portal> {
  container?: Element | DocumentFragment | null;
  children?: React.ReactNode;
}

interface DialogClosePropsType extends React.ComponentProps<typeof DialogPrimitive.Close> {
  asChild?: boolean;
  children?: React.ReactNode;
}

interface DialogOverlayPropsType extends React.ComponentProps<typeof DialogPrimitive.Overlay> {
  asChild?: boolean;
  children?: React.ReactNode;
  className?: string;
  forceMount?: true;
}

interface DialogContentPropsType extends React.ComponentProps<typeof DialogPrimitive.Content> {
  asChild?: boolean;
  forceMount?: true;
  onOpenAutoFocus?: (event: Event) => void;
  onCloseAutoFocus?: (event: Event) => void;
  onEscapeKeyDown?: (event: Event) => void;
  onPointerDownOutside?: (event: Event) => void;
  onInteractOutside?: (event: Event) => void;
  showCloseButton?: boolean;
  className?: string;
  overlayClassName?: string;
  isLoading?: boolean;
  useOutsideCloseOverlay?: boolean;
}

interface DialogHeaderPropsType extends React.ComponentProps<"div"> {
  asChild?: boolean;
  children?: React.ReactNode;
  className?: string;
}

interface DialogFooterPropsType extends React.ComponentProps<"div"> {
  asChild?: boolean;
  children?: React.ReactNode;
  className?: string;
}

interface DialogTitlePropsType extends React.ComponentProps<typeof DialogPrimitive.Title> {
  asChild?: boolean;
  children?: React.ReactNode;
  className?: string;
}

interface DialogDescriptionPropsType extends React.ComponentProps<typeof DialogPrimitive.Description> {
  asChild?: boolean;
  children?: React.ReactNode;
  className?: string;
}

function Dialog({ ...props }: DialogPropsType) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: DialogTriggerPropsType) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPortalPropsType) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: DialogClosePropsType) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: DialogOverlayPropsType) {
  return <DialogPrimitive.Overlay data-slot="dialog-overlay" className={cn("data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-55 bg-black/80 max-lg:bg-transparent", className)} {...props} />;
}

function DialogContent({ className, children, showCloseButton = true, isLoading = false, useOutsideCloseOverlay = true, ...props }: DialogContentPropsType) {
  const { overlayClassName, ...rest } = props;

  const escapeKeyDownHandler = React.useCallback(
    (event: KeyboardEvent) => {
      if ((event.key === "Escape" && isLoading) || !useOutsideCloseOverlay) {
        event.preventDefault();
      }
    },
    [isLoading, useOutsideCloseOverlay],
  );

  const pointerDownOutsideHandler = React.useCallback(
    (event: Event) => {
      if (isLoading || !useOutsideCloseOverlay) {
        event.preventDefault();
      }
    },
    [isLoading, useOutsideCloseOverlay],
  );

  const interactOutsideHandler = React.useCallback(
    (event: Event) => {
      if (isLoading || !useOutsideCloseOverlay) {
        event.preventDefault();
      }
    },
    [isLoading, useOutsideCloseOverlay],
  );

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay className={cn(overlayClassName)} />
      <DialogPrimitive.Content data-slot="dialog-content" className={cn("fixed top-[50%] left-[50%] z-55 max-w-full translate-x-[-50%] translate-y-[-50%] overflow-hidden bg-white shadow-[0px_4px_6px_rgba(0,0,0,0.09)] duration-200", "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95", "max-lg:h-dvh max-lg:w-full", "lg:h-auto lg:max-w-[900px] lg:min-w-[486px] lg:rounded-lg", className)} onEscapeKeyDown={escapeKeyDownHandler} onPointerDownOutside={pointerDownOutsideHandler} onInteractOutside={interactOutsideHandler} {...rest}>
        <div className="relative w-full pt-[56px] max-lg:h-full lg:max-h-[90vh] lg:overflow-y-auto lg:pt-0">
          <div className={cn("px-[20px] pb-[95px] max-lg:h-full max-lg:overflow-y-auto max-lg:pt-[20px] lg:h-auto lg:px-[30px] lg:pb-[20px]")}>{children}</div>

          {showCloseButton && (
            <DialogPrimitive.Close data-slot="dialog-close" className={cn("ring-offset-background absolute top-[10px] right-[10px] z-5 cursor-pointer focus:outline-hidden disabled:pointer-events-none", "focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground", "lg:top-[20px] lg:right-[30px]", "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[30px]")}>
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </div>
        {isLoading && <Loading />}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: DialogHeaderPropsType) {
  return <div data-slot="dialog-header" className={cn("absolute top-0 left-0 z-1 mb-[20px] w-full bg-white px-[20px] max-lg:pt-[56px] lg:relative lg:px-0 lg:pt-[44px]", className)} {...props} />;
}

function DialogFooter({ className, ...props }: DialogFooterPropsType) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "fixed bottom-0 left-0 flex w-full items-end justify-between",
        "max-lg:[&_button]:h-[52px] max-lg:[&_button]:rounded-none max-lg:[&_button]:border-0 max-lg:[&_button]:border-t-1", // 버튼 너비 제어는 common.css에 위치
        "lg:static lg:justify-center lg:gap-[10px] lg:py-[20px]",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({ className, children, ...props }: DialogTitlePropsType) {
  return (
    <DialogPrimitive.Title data-slot="dialog-title" className={cn("absolute top-0 left-0 w-full px-[20px]", "lg:static lg:w-full lg:px-0", className)} {...props}>
      <div className={cn("flex items-center border-b border-gray-200 text-[17px] font-bold max-lg:h-[56px] lg:pb-[11px] lg:text-[24px]")}>{children}</div>
    </DialogPrimitive.Title>
  );
}

function DialogDescription({ className, ...props }: DialogDescriptionPropsType) {
  return <DialogPrimitive.Description data-slot="dialog-description" className={cn(className)} {...props} />;
}

export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger };
